import { hasFields, isUIExtension } from '@getjust/checkout-extensions';

import type { ActionToExecute, ExtensionExecutionContext, ExtensionWithFields, SimpleField } from '../types';
import type { CheckoutExtension } from '@getjust/checkout-extensions';

import { ExtensionConditionEvaluator } from './ExtensionConditionEvaluator';

/**
 *  ExtensionManager
 * No classes, no factories, just pure functions processing JSON config
 */
export class ExtensionManager {
  private extensions: CheckoutExtension[] = [];
  private conditionEvaluator: ExtensionConditionEvaluator;

  constructor() {
    this.conditionEvaluator = new ExtensionConditionEvaluator();
  }

  loadExtensions(extensionConfigs: CheckoutExtension[]): void {
    this.extensions = extensionConfigs.filter((ext) => ext.enabled);
  }

  getExtensionsByContext(context: string): CheckoutExtension[] {
    return this.extensions.filter((ext) => ext.context === context);
  }

  evaluateConditions(conditions: any, context: ExtensionExecutionContext): boolean {
    return this.conditionEvaluator.evaluate(conditions, context);
  }

  /**
   * Get visible fields - flattened version (no duplication)
   */
  getVisibleFields(context: ExtensionExecutionContext): SimpleField[] {
    return this.getExtensionsWithFields(context).flatMap((ewf) => ewf.fields);
  }

  getExtensionsWithFields(context: ExtensionExecutionContext): ExtensionWithFields[] {
    const extensionsWithFields: ExtensionWithFields[] = [];

    for (const extension of this.extensions) {
      // Check extension conditions
      if (extension.when && !this.conditionEvaluator.evaluate(extension.when, context)) {
        continue;
      }

      // Only UI extensions have fields
      if (isUIExtension(extension) && hasFields(extension)) {
        const visibleFields: SimpleField[] = [];

        for (const field of extension.fields) {
          // Check field conditions
          if (field.when && !this.conditionEvaluator.evaluate(field.when, context)) {
            continue;
          }

          visibleFields.push({
            id: field.id,
            type: field.type,
            label: field.label,
            defaultChecked: field.type === 'checkbox' ? field.defaultChecked : undefined,
            actions: field.actions,
            defaultLocale: extension.defaultLocale,
            title: extension.title,
            description: extension.description,
          });
        }

        if (visibleFields.length > 0) {
          extensionsWithFields.push({
            extension,
            fields: visibleFields,
          });
        }
      }
    }

    return extensionsWithFields;
  }

  executeActions(trigger: string, context: ExtensionExecutionContext): ActionToExecute[] {
    const actions: ActionToExecute[] = [];

    console.warn(`[ExtensionManager] executeActions for trigger: ${trigger}`, {
      contextFieldValues: context.fieldValues,
    });

    for (const extension of this.extensions) {
      // Check extension conditions
      if (extension.when && !this.conditionEvaluator.evaluate(extension.when, context)) {
        continue;
      }

      // Handle global extensions (have actions at root level)
      if (extension.context === 'global' && 'actions' in extension) {
        for (const action of extension.actions) {
          if (action.trigger === trigger) {
            // Check action conditions (only for discount-application)
            if (action.trigger === 'on-discount-application' && 'when' in action && action.when) {
              if (!this.conditionEvaluator.evaluate(action.when, context)) {
                continue;
              }
            }

            const actionData = this.createActionData(action.type, action.config, null);
            if (actionData) {
              actions.push({
                type: action.type as any,
                data: actionData,
              });
            }
          }
        }
      }

      // Handle UI extensions (have actions in fields)
      if (isUIExtension(extension) && hasFields(extension)) {
        for (const field of extension.fields) {
          // Check field conditions
          if (field.when && !this.conditionEvaluator.evaluate(field.when, context)) {
            continue;
          }

          if (field.actions) {
            for (const action of field.actions) {
              if (action.trigger === trigger) {
                // Check action conditions
                if (action.trigger === 'on-discount-application' && 'when' in action && action.when) {
                  if (!this.conditionEvaluator.evaluate(action.when, context)) {
                    continue;
                  }
                }

                const fieldValue = context.fieldValues[field.id];

                // For checkbox fields, only execute if checked (unless useFieldValue is true)
                const actionConfig = action.config as Record<string, unknown> | undefined;
                if (
                  field.type === 'checkbox' &&
                  !(actionConfig && 'useFieldValue' in actionConfig && actionConfig.useFieldValue)
                ) {
                  if (!fieldValue) continue;
                }

                const actionData = this.createActionData(action.type, action.config, fieldValue);
                if (actionData) {
                  actions.push({
                    type: action.type as any,
                    data: actionData,
                    fieldId: field.id,
                    fieldValue,
                  });
                }
              }
            }
          }
        }
      }
    }

    return actions;
  }

  private createActionData(actionType: string, config: any, fieldValue: unknown): any {
    switch (actionType) {
      case 'add-order-custom-attribute': {
        let resolvedValue: string;
        if (config.useFieldValue) {
          // Handle boolean values properly (checkbox)
          if (typeof fieldValue === 'boolean') {
            resolvedValue = fieldValue ? 'true' : 'false';
          } else if (fieldValue != null) {
            resolvedValue = String(fieldValue);
          } else {
            // Default value for undefined/null field values
            resolvedValue = 'false';
          }
        } else {
          resolvedValue = config.value;
        }

        return {
          key: config.key,
          value: resolvedValue,
        };
      }

      case 'add-product': {
        const productData: {
          quantity: number;
          variantId: string;
          customAttributes?: Array<{ key: string; value: string }>;
        } = {
          variantId: config.variantId,
          quantity: config.quantity || 1,
        };

        // Include custom attributes if provided in config
        if (
          config.customAttributes &&
          Array.isArray(config.customAttributes) &&
          config.customAttributes.length > 0
        ) {
          productData.customAttributes = config.customAttributes;
        }

        return productData;
      }

      default:
        return config;
    }
  }

  clear(): void {
    this.extensions = [];
  }
}
